import { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Image, ImageSourcePropType, Modal, PanResponder, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ActionHeader } from '../components/AppHeader';
import colors from '../theme';
import { RootStackParamList } from '../navigation/RootNavigator';
import { usePlayback } from '../context/PlaybackContext';
import { isLoggedIn } from '../api/auth';
import { getLibraryState, toggleFavorite } from '../api/library';

type Props = NativeStackScreenProps<RootStackParamList, 'Player'>;

const coverImage = require('../../assets/aural-continue-thumb.png');
const waveHeights = [22, 45, 49, 38, 52, 28, 33, 12, 55, 5, 20, 50, 39, 27, 19, 29, 48, 29, 12, 17, 48, 29, 21, 50, 7, 34, 53, 29, 32, 10, 33, 22, 53, 26, 50, 5, 10, 32, 15, 14, 38, 37, 51, 6, 50, 36, 26, 41, 40, 17, 38, 25, 41, 38, 28, 15, 21, 56, 42, 16, 10, 12, 12, 30, 19, 35, 24, 19, 53, 49, 10, 32, 29, 47, 45, 52, 23, 36, 27, 42, 53, 43, 35, 46, 21, 20];

function imageSource(url: string | null | undefined): ImageSourcePropType {
  return url ? { uri: url } : coverImage;
}

function formatTime(seconds?: number | null) {
  if (!seconds) return '00:00';
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

function PlayerSkeleton() {
  return (
    <>
      <View style={[styles.cover, styles.skeletonBlock]} />
      <View style={[styles.skeletonBlock, styles.skeletonTitle]} />
      <View style={[styles.skeletonBlock, styles.skeletonAuthor]} />
    </>
  );
}

export default function PlayerScreen({ navigation, route }: Props) {
  const playback = usePlayback();
  const params = route.params;
  const dragY = useRef(new Animated.Value(0)).current;
  const [chaptersVisible, setChaptersVisible] = useState(false);
  const [favorite, setFavorite] = useState(false);

  useEffect(() => {
    const isSameTrack = Boolean(
      playback.book
      && (params?.id ? params.id === playback.book.id : true)
      && (params?.slug ? params.slug === playback.book.slug : true),
    );

    if (isSameTrack) {
      playback.setMinimized(false);
    } else {
      playback.load(params);
    }
  }, [params?.id, params?.slug]);

  useEffect(() => {
    if (!playback.book || !isLoggedIn()) {
      setFavorite(false);
      return;
    }

    getLibraryState(playback.book.id)
      .then((state) => setFavorite(state.is_favorite))
      .catch(() => setFavorite(false));
  }, [playback.book?.id]);

  const handleFavorite = async () => {
    if (!playback.book) {
      return;
    }

    if (!isLoggedIn()) {
      playback.stop();
      navigation.navigate('Login');
      return;
    }

    const state = await toggleFavorite(playback.book.id).catch(() => null);
    if (state) {
      setFavorite(state.is_favorite);
    }
  };

  const handleMinimize = () => {
    if (playback.book) {
      playback.setMinimized(true);
    }

    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('Home');
    }
  };

  const resetDrag = () => {
    Animated.spring(dragY, {
      toValue: 0,
      useNativeDriver: true,
      tension: 90,
      friction: 12,
    }).start();
  };

  const finishMinimize = () => {
    Animated.timing(dragY, {
      toValue: 360,
      duration: 180,
      useNativeDriver: true,
    }).start(() => {
      dragY.setValue(0);
      handleMinimize();
    });
  };

  const panResponder = useMemo(
    () => PanResponder.create({
      onMoveShouldSetPanResponder: (_, gesture) => Math.abs(gesture.dy) > 12 && Math.abs(gesture.dy) > Math.abs(gesture.dx),
      onPanResponderGrant: () => {
        dragY.stopAnimation();
      },
      onPanResponderMove: (_, gesture) => {
        dragY.setValue(Math.max(gesture.dy, 0));
      },
      onPanResponderRelease: (_, gesture) => {
        if (gesture.dy > 80) {
          finishMinimize();
        } else {
          resetDrag();
        }
      },
      onPanResponderTerminate: resetDrag,
    }),
    [dragY, playback.book],
  );

  const cover = imageSource(playback.cover);
  const animatedStyle = {
    opacity: dragY.interpolate({
      inputRange: [0, 220],
      outputRange: [1, 0.82],
      extrapolate: 'clamp' as const,
    }),
    transform: [
      {
        translateY: dragY.interpolate({
          inputRange: [0, 360],
          outputRange: [0, 180],
          extrapolate: 'clamp' as const,
        }),
      },
      {
        scale: dragY.interpolate({
          inputRange: [0, 220],
          outputRange: [1, 0.92],
          extrapolate: 'clamp' as const,
        }),
      },
    ],
  };
  const overlayStyle = {
    opacity: dragY.interpolate({
      inputRange: [0, 180],
      outputRange: [0, 0.28],
      extrapolate: 'clamp' as const,
    }),
  };

  return (
    <View style={styles.screen}>
      <Animated.View pointerEvents="none" style={[styles.scrim, overlayStyle]} />
      <Animated.View style={[styles.playerSheet, animatedStyle]} {...panResponder.panHandlers}>
        <View style={styles.dragHandle} />
        <View style={styles.topPanel}>
          <ActionHeader onBack={handleMinimize} rightIcons={['ellipsis-horizontal']} />
          {playback.loading || !playback.book ? (
            <PlayerSkeleton />
          ) : (
            <>
              <Image source={cover} style={styles.cover} resizeMode="cover" />
              <Text style={styles.title}>{playback.title}</Text>
              <Text style={styles.author}>{playback.author}</Text>
              {playback.chapterTitle ? <Text style={styles.chapterName}>{playback.chapterTitle}</Text> : null}
              <Text style={styles.hint}>{'Vu\u1ed1t xu\u1ed1ng \u0111\u1ec3 thu nh\u1ecf'}</Text>
            </>
          )}
        </View>
        <View style={styles.controlBar}>
          <View style={styles.timeRow}>
            <Text style={styles.time}>{formatTime(playback.position)}</Text>
            <Text style={styles.time}>{formatTime(playback.duration)}</Text>
          </View>
          <View style={styles.waveform}>
            {waveHeights.map((height, index) => (
              <Pressable
                key={`${height}-${index}`}
                onPress={() => playback.seekToRatio(index / Math.max(waveHeights.length - 1, 1))}
                hitSlop={4}
              >
                <View
                  style={[
                    styles.wave,
                    {
                      height,
                      backgroundColor: index / waveHeights.length <= playback.progress ? colors.button : colors.muted,
                    },
                  ]}
                />
              </Pressable>
            ))}
          </View>
          <View style={styles.controls}>
            <Pressable style={styles.skipBlock} onPress={() => playback.seekBy(-15)}>
              <Text style={styles.skipText}>-15 giây</Text>
              <View style={styles.skipLine} />
            </Pressable>
            <Pressable onPress={playback.hasChapters ? playback.previousChapter : () => playback.seekBy(-30)} hitSlop={10}>
              <Ionicons name="play-skip-back" size={24} color={colors.buttonText} />
            </Pressable>
            <Pressable style={styles.pause} onPress={playback.togglePlay}>
              <Ionicons name={playback.isPlaying ? 'pause' : 'play'} size={24} color={colors.textPrimary} />
            </Pressable>
            <Pressable onPress={playback.hasChapters ? playback.nextChapter : () => playback.seekBy(30)} hitSlop={10}>
              <Ionicons name="play-skip-forward" size={24} color={colors.buttonText} />
            </Pressable>
            <Pressable style={styles.skipBlock} onPress={() => playback.seekBy(15)}>
              <Text style={styles.skipText}>+15 giây</Text>
              <View style={styles.skipLine} />
            </Pressable>
          </View>
          <View style={styles.quickActions}>
            <Text style={styles.quickText}>1x</Text>
            <Ionicons name="timer-outline" size={24} color={colors.buttonText} />
            <Ionicons name="download-outline" size={24} color={colors.buttonText} />
            <Pressable onPress={handleFavorite} hitSlop={10}>
              <Ionicons name={favorite ? 'heart' : 'heart-outline'} size={24} color={favorite ? colors.yellow : colors.buttonText} />
            </Pressable>
            <Pressable onPress={() => playback.hasChapters && setChaptersVisible(true)} hitSlop={10}>
              <Ionicons name="list-outline" size={24} color={playback.hasChapters ? colors.buttonText : colors.muted} />
            </Pressable>
          </View>
        </View>
      </Animated.View>

      <Modal visible={chaptersVisible} transparent animationType="slide" onRequestClose={() => setChaptersVisible(false)}>
        <Pressable style={styles.sheetBackdrop} onPress={() => setChaptersVisible(false)}>
          <Pressable style={styles.chapterSheet} onPress={(event) => event.stopPropagation()}>
            <View style={styles.sheetHandle} />
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Danh sách tập</Text>
              <Pressable onPress={() => setChaptersVisible(false)} hitSlop={10}>
                <Ionicons name="close" size={22} color={colors.textPrimary} />
              </Pressable>
            </View>
            <ScrollView showsVerticalScrollIndicator={false}>
              {playback.chapters.map((chapter, index) => {
                const active = playback.activeChapterIndex === index;
                return (
                  <Pressable
                    key={chapter.id}
                    style={[styles.chapterItem, active && styles.chapterItemActive]}
                    onPress={() => {
                      playback.selectChapter(index);
                      setChaptersVisible(false);
                    }}
                  >
                    <View style={styles.chapterIndex}>
                      <Text style={[styles.chapterIndexText, active && styles.chapterTextActive]}>{index + 1}</Text>
                    </View>
                    <View style={styles.chapterTextBlock}>
                      <Text style={[styles.chapterTitle, active && styles.chapterTextActive]} numberOfLines={2}>{chapter.name}</Text>
                      <Text style={styles.chapterMeta}>{formatTime(chapter.duration)}</Text>
                    </View>
                    {active ? <Ionicons name="volume-high" size={18} color={colors.button} /> : null}
                  </Pressable>
                );
              })}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#0B0B0C' },
  scrim: { ...StyleSheet.absoluteFillObject, backgroundColor: colors.background },
  playerSheet: { flex: 1, backgroundColor: '#0B0B0C' },
  dragHandle: { position: 'absolute', top: 12, alignSelf: 'center', width: 44, height: 5, borderRadius: 100, backgroundColor: '#34343A', zIndex: 10 },
  topPanel: { height: 600, backgroundColor: colors.background, borderBottomLeftRadius: 24, borderBottomRightRadius: 24, paddingTop: 62, paddingHorizontal: 24 },
  cover: { width: 240, height: 240, borderRadius: 8, alignSelf: 'center', marginTop: 54 },
  title: { color: colors.textPrimary, fontSize: 20, fontWeight: '600', textAlign: 'center', marginTop: 44 },
  author: { color: colors.textSecondary, fontSize: 14, fontWeight: '500', textAlign: 'center', marginTop: 16 },
  chapterName: { color: colors.button, fontSize: 13, fontWeight: '600', textAlign: 'center', marginTop: 10 },
  hint: { color: colors.textSecondary, fontSize: 12, textAlign: 'center', marginTop: 14 },
  timeRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 },
  time: { color: colors.textSecondary, fontSize: 12 },
  waveform: { flexDirection: 'row', alignItems: 'center', gap: 2, height: 56, marginHorizontal: -7, marginBottom: 28 },
  wave: { width: 2, borderRadius: 10 },
  controlBar: { position: 'absolute', left: 24, right: 24, bottom: 34 },
  controls: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 36 },
  skipBlock: { width: 45, gap: 8 },
  skipText: { color: colors.buttonText, fontSize: 12, fontWeight: '600' },
  skipLine: { height: 2, backgroundColor: colors.button, borderRadius: 10 },
  pause: { width: 60, height: 60, borderRadius: 30, backgroundColor: colors.buttonText, alignItems: 'center', justifyContent: 'center' },
  quickActions: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  quickText: { color: colors.buttonText, fontSize: 20, fontWeight: '500' },
  skeletonBlock: { backgroundColor: '#ECEEF2' },
  skeletonTitle: { width: 190, height: 22, borderRadius: 6, alignSelf: 'center', marginTop: 44 },
  skeletonAuthor: { width: 96, height: 14, borderRadius: 6, alignSelf: 'center', marginTop: 16 },
  sheetBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(11, 11, 12, 0.42)',
    justifyContent: 'flex-end',
  },
  chapterSheet: {
    maxHeight: '64%',
    backgroundColor: colors.background,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 28,
  },
  sheetHandle: {
    width: 44,
    height: 5,
    borderRadius: 100,
    backgroundColor: colors.border,
    alignSelf: 'center',
    marginBottom: 16,
  },
  sheetHeader: {
    height: 36,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  sheetTitle: {
    color: colors.textPrimary,
    fontSize: 18,
    fontWeight: '700',
  },
  chapterItem: {
    minHeight: 64,
    borderRadius: 10,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 10,
    gap: 12,
  },
  chapterItemActive: {
    backgroundColor: colors.accentSoft,
  },
  chapterIndex: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chapterIndexText: {
    color: colors.textSecondary,
    fontSize: 12,
    fontWeight: '700',
  },
  chapterTextBlock: {
    flex: 1,
  },
  chapterTitle: {
    color: colors.textPrimary,
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 20,
    marginBottom: 4,
  },
  chapterTextActive: {
    color: colors.button,
  },
  chapterMeta: {
    color: colors.textSecondary,
    fontSize: 12,
  },
});
